import { Observable } from "light-observable";
import { createSubject } from "light-observable/observable";
import { valueWritable } from "..";

export { default as Log } from "./Log.svelte";
export { default as LogHeader } from "./LogHeader.svelte";

export type LogLine<T = unknown> = { level: string; message: string; time: number, args?: any[]; data?: T };
export type FilterLogFunction<T = unknown> = (line: LogLine<T>) => boolean;

export function createLogger<LogData>() {
    const [stream, sink] = createSubject<LogLine<LogData>>();

    const lines = [{ level: "INFO", message: "Initialized", time: Date.now() }] as LogLine<LogData>[];
    stream.subscribe((line) => lines.push(line));

    function addLine(line: LogLine<LogData>) {
        sink.next(line);
    }

    const levels = ["DEBUG", "INFO", "WARN", "ERROR"];
    const colors = {
        DEBUG: "#bf27f1",
        INFO: "#16d5a2",
        WARN: "#F1F127",
        ERROR: "#DB3707",
    } as Record<string, string>;

    return {
        log: (level: string, message: string, ...args: any[]) => addLine({ level, message, args, time: Date.now() }),
        addStream: <InputData>(
            stream: Observable<InputData>,
            parser: (data: InputData) => LogLine<LogData>
        ) => {
            stream.subscribe((data) => sink.next(parser(data)));
        },
        getLines: () => lines,
        stream,

        info: (message: string, ...args: any[]) => addLine({ level: "INFO", message, args, time: Date.now() }),
        debug: (message: string, ...args: any[]) => addLine({ level: "DEBUG", message, args, time: Date.now() }),
        error: (message: string, ...args: any[]) => addLine({ level: "ERROR", message, args, time: Date.now() }),

        levels,
        colors,
        addLevel: (level: string, color: string) => {
            if (levels.includes(level)) return;
            levels.push(level);
            colors[level] = color;
        },

        filter: valueWritable((line: LogLine<LogData>) => true),
    };
}

export type Logger<LogData = unknown> = ReturnType<typeof createLogger<LogData>>;
